import React, { useMemo, useState } from "react";
import "../../css/calculators/MortgageBorrow.css";
import SecondaryButton from "../components/buttons/SecondaryButton";
import PrimaryButton from "../components/buttons/PrimaryButton";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";
import CustomSelect from "../components/CustomSelect";

const initialState = {
  propertyValue: "",
  otherPropertyValue: "",
  savings: "",
  investments: "",
  otherAssets: "",
  lifeInsurance: "",
  trustValue: "",
  assetsToSpouse: "",
  maritalStatus: "widowed",
  remainingMortgage: "",
  loan: "",
  otherCredit: "",
  creditCardDebt: "",
  overdrafts: "",
  giftsLast7Years: "",
};

const maritalStatusOptions = [
//   { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Unmarried / Divorced" },
  { value: "widowed", label: "Widowed" },
];

const parseAmount = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export default function InheritanceTaxCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [hasCalculated, setHasCalculated] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCalculate = () => {
    setHasCalculated(true);
  };

  const handleClear = () => {
    setFormData(initialState);
    setHasCalculated(false);
  };

  const results = useMemo(() => {
    const assets = {
      propertyValue: parseAmount(formData.propertyValue),
      otherPropertyValue: parseAmount(formData.otherPropertyValue),
      savings: parseAmount(formData.savings),
      investments: parseAmount(formData.investments),
      otherAssets: parseAmount(formData.otherAssets),
      lifeInsurance: parseAmount(formData.lifeInsurance),
      trustValue: parseAmount(formData.trustValue),
      giftsLast7Years: parseAmount(formData.giftsLast7Years),
    };

    const liabilities = {
      remainingMortgage: parseAmount(formData.remainingMortgage),
      loan: parseAmount(formData.loan),
      otherCredit: parseAmount(formData.otherCredit),
      creditCardDebt: parseAmount(formData.creditCardDebt),
      overdrafts: parseAmount(formData.overdrafts),
    };

    const totalAssets =
      assets.propertyValue +
      assets.otherPropertyValue +
      assets.savings +
      assets.investments +
      assets.otherAssets +
      assets.lifeInsurance +
      assets.trustValue +
      assets.giftsLast7Years;

    const totalLiabilities =
      liabilities.remainingMortgage +
      liabilities.loan +
      liabilities.otherCredit +
      liabilities.creditCardDebt +
      liabilities.overdrafts;

    const netWorth = Math.max(totalAssets - totalLiabilities, 0);

    const assetsPassingToSpouse = parseAmount(formData.assetsToSpouse);
    const mainResidencePassedToDescendants = assets.propertyValue > 0;

    const standardNilRateBand = 325000;
    const residenceNilRateBand = mainResidencePassedToDescendants ? 175000 : 0;

    const spouseExemption =
      formData.maritalStatus === "married" ? assetsPassingToSpouse : 0;

    const totalAllowances =
      standardNilRateBand + residenceNilRateBand + spouseExemption;

    const taxableEstate = Math.max(netWorth - totalAllowances, 0);
    const inheritanceTax = taxableEstate * 0.4;

    return {
      totalAssets,
      totalLiabilities,
      netWorth,
      taxableEstate,
      inheritanceTax,
      totalAllowances,
    };
  }, [formData]);

  return (
    <div className="borrow-calculator-page">
      <div className="borrow-form-wrap">
        <div className="title primary">WHAT YOU OWN</div>

        <div className="borrow-field-group">
          <label className="borrow-label">Property value (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="propertyValue"
              value={formData.propertyValue}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  propertyValue: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Other property value (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="otherPropertyValue"
              value={formData.otherPropertyValue}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  otherPropertyValue: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Savings (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="savings"
              value={formData.savings}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  savings: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Investments (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="investments"
              value={formData.investments}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  investments: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">
            Other possessions or assets (e.g. vehicles, house contents) (£)
          </label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="otherAssets"
              value={formData.otherAssets}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  otherAssets: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">
            Life insurance pay outs (if not in trust) (£)
          </label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="lifeInsurance"
              value={formData.lifeInsurance}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  lifeInsurance: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">
            Value of any trusts to which you are a life tenant (£)
          </label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="trustValue"
              value={formData.trustValue}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  trustValue: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">
            Value of assets (other than main residence) to pass to spouse (£)
          </label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="assetsToSpouse"
              value={formData.assetsToSpouse}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  assetsToSpouse: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">What's your marital status?</label>
          <div className="select-wrapper">
            {/* <select
              name="maritalStatus"
              value={formData.maritalStatus}
              onChange={handleChange}
              className="borrow-input"
            >
              {maritalStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> */}
            <CustomSelect
              name="maritalStatus"
              options={maritalStatusOptions}
              value={formData.maritalStatus}
              onChange={handleChange}
              placeholder="Please select your marital status"
            />
          </div>
        </div>

        <div className="title primary" style={{ marginTop: 28 }}>
          WHAT YOU OWE
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Remaining mortgage (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="remainingMortgage"
              value={formData.remainingMortgage}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  remainingMortgage: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Loan (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="loan"
              value={formData.loan}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  loan: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Other credit agreements (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="otherCredit"
              value={formData.otherCredit}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  otherCredit: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Credit card debt (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="creditCardDebt"
              value={formData.creditCardDebt}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  creditCardDebt: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Overdrafts (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="overdrafts"
              value={formData.overdrafts}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  overdrafts: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">
            Value of non-exempt gifts made in the last 7 years (£)
          </label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="giftsLast7Years"
              value={formData.giftsLast7Years}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  giftsLast7Years: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 24,
            justifyContent: "flex-end",
          }}
        >
          <SecondaryButton onClick={handleClear} text="Clear" />
          <PrimaryButton onClick={handleCalculate} text="Calculate" />
        </div>
      </div>

      {(hasCalculated || true) && (
        <div className="borrow-result-wrap result-container">
       
            <div className="title">Result</div>

            <div className="result-section">
              <div className="result-heading">Total assets:</div>
              <div className="result-value">
                {formatCurrency(results.totalAssets)}
              </div>
              <p className="result-copy">(Everything you own)</p>
            </div>

            <div className="result-section">
              <div className="result-heading">Total liabilities:</div>
              <div className="result-value">
                {formatCurrency(results.totalLiabilities)}
              </div>
              <p className="result-copy">(Everything you owe)</p>
            </div>

            <div className="result-section">
              <div className="result-heading">Net worth:</div>
              <div className="result-value">
                {formatCurrency(results.netWorth)}
              </div>
              <p className="result-copy">
                (Everything you own minus everything you owe)
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">
                Amount exposed to inheritance tax:
              </div>
              <div className="result-value">
                {formatCurrency(results.taxableEstate)}
              </div>
              <p className="result-copy">
                Estimated inheritance tax at 40%:{" "}
                <strong>{formatCurrency(results.inheritanceTax)}</strong>
              </p>
            </div>

            <div className="result-divider" />

            <div className="result-note-title">PLEASE NOTE:</div>
            <p className="result-note-copy">
              The calculator makes three assumptions:
            </p>
            <p className="result-note-copy">
              • The main residence will be passed to a direct descendant (not a
              spouse)
              <br />• You have not inherited some or all of a spouse’s nil rate
              band or residence nil rate band
              <br />• No assets will qualify for Business Property Relief
            </p>
            <p className="result-note-copy">
              If any of the above assumptions are not true for your
              circumstances then the result could be significantly altered, and
              you should seek financial advice.
            </p>
            <p className="result-note-copy">
              If you have recently downsized or sold your main residence then
              you could still benefit from more or some of the Residence Nil
              Rate Band if the previous residence would have qualified; this
              calculator does not account for this.
            </p>
            <p className="result-note-copy">
              Gifts made in the last 7 years could be subject to inheritance tax
              on the recipient.
            </p>
       
        </div>
      )}
    </div>
  );
}
